'use client';

import { cn } from '@/lib/utils';
import { ArrowRight } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';
import showcaseStyles from '@/components/section/scrolling-showcase/scrolling-showcase.module.scss';
import tickerStyles from '@/components/section/scrolling-showcase/scrolling-ticker.module.scss';

export type ScrollingShowcaseWrapperProps = {
  children?: React.ReactNode;
  logoDisplay?: 'circular' | 'natural';
  enableCarousel?: boolean;
  enableAutoscroll?: boolean;
  scrollSpeed?: number;
  logosPerSlide?: number;
  mobileLogosPerSlide?: number;
  logoMaxHeight?: number;
  logoSpacing?: number;
  centerDotsOnMobile?: boolean;
};

export const ScrollingShowcaseWrapper = ({
  children,
  logoDisplay = 'circular',
  enableCarousel = true,
  enableAutoscroll = false,
  scrollSpeed = 3,
  logosPerSlide,
  mobileLogosPerSlide,
  logoMaxHeight,
  logoSpacing,
  centerDotsOnMobile = false,
}: ScrollingShowcaseWrapperProps) => {
  const childrenArray = React.Children.toArray(children);
  const slideCount = childrenArray.length;

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progressKey, setProgressKey] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoscrollMs = scrollSpeed * 1000;

  const gap = Number(logoSpacing) || 20;
  const maxH = Number(logoMaxHeight) || 0;

  const [sliderRef, instanceRef] = useKeenSlider({
    loop: true,
    renderMode: 'performance',
    drag: true,
    slides: (containerWidth: number) => {
      const isMobile = containerWidth < 792;
      const perSlide = isMobile && mobileLogosPerSlide ? mobileLogosPerSlide : logosPerSlide;

      if (perSlide) {
        const totalGap = gap * (perSlide - 1);
        const slideWidth = (containerWidth - totalGap) / perSlide;
        return childrenArray.map(() => ({
          size: slideWidth / containerWidth,
          spacing: gap / containerWidth,
        }));
      }
      const defaultWidth = containerWidth >= 1024 ? 166 : 116;
      const slideWidth = maxH && maxH < defaultWidth ? maxH : defaultWidth;
      return childrenArray.map(() => ({
        size: slideWidth / containerWidth,
        spacing: gap / containerWidth,
      }));
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel);
      setProgressKey((k) => k + 1);
    },
  });

  // Re-calc keen-slider when layout-affecting props change
  useEffect(() => {
    instanceRef.current?.update();
  }, [gap, maxH, logosPerSlide, mobileLogosPerSlide, instanceRef]);

  useEffect(() => {
    if (!enableAutoscroll || isPaused) return;
    timerRef.current = setInterval(() => {
      instanceRef.current?.next();
    }, autoscrollMs);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [enableAutoscroll, isPaused, autoscrollMs, instanceRef]);

  const handleMouseEnter = () => {
    if (enableAutoscroll) setIsPaused(true);
  };
  const handleMouseLeave = () => {
    if (enableAutoscroll) {
      setIsPaused(false);
      setProgressKey((k) => k + 1);
    }
  };

  const goPrev = useCallback(() => {
    instanceRef.current?.prev();
    if (enableAutoscroll) {
      setIsPaused(false);
      setProgressKey((k) => k + 1);
    }
  }, [instanceRef, enableAutoscroll]);

  const goNext = useCallback(() => {
    instanceRef.current?.next();
    if (enableAutoscroll) {
      setIsPaused(false);
      setProgressKey((k) => k + 1);
    }
  }, [instanceRef, enableAutoscroll]);

  if (slideCount === 0) return null;

  // Inline style applied to each logo's inner wrapper to constrain height
  const logoInnerStyle: React.CSSProperties | undefined = maxH
    ? ({ '--logo-max-height': `${maxH}px` } as React.CSSProperties)
    : undefined;

  return (
    <div style={{ width: '100%' }}>
      {enableCarousel ? (
        <div className={tickerStyles.outer}>
          <div className={tickerStyles.wrapper}>
            <button onClick={goPrev} className={tickerStyles.arrow} aria-label="Previous logos">
              <ArrowRight width={24} height={24} className={tickerStyles.arrow__icon_prev} />
            </button>

            <div
              className={tickerStyles.track_wrapper}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div ref={sliderRef} className={cn('keen-slider', tickerStyles.track)}>
                {childrenArray.map((child, i) => (
                  <div
                    key={i}
                    className={cn(
                      'keen-slider__slide',
                      tickerStyles.logo_item,
                      tickerStyles[`logo_item--${logoDisplay}`],
                    )}
                  >
                    <div className={tickerStyles.logo_inner} style={logoInnerStyle}>
                      {child}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button onClick={goNext} className={tickerStyles.arrow} aria-label="Next logos">
              <ArrowRight width={24} height={24} />
            </button>
          </div>

          {/* Pagination dots */}
          <div
            className={cn(
              tickerStyles.dots,
              centerDotsOnMobile && tickerStyles['dots--center-mobile'],
            )}
          >
            {childrenArray.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  instanceRef.current?.moveToIdx(i);
                  if (enableAutoscroll) {
                    setIsPaused(false);
                    setProgressKey((k) => k + 1);
                  }
                }}
                className={cn(
                  tickerStyles.dot,
                  i === currentSlide && tickerStyles['dot--active'],
                  i === currentSlide && enableAutoscroll && tickerStyles['dot--autoscroll'],
                )}
                aria-label={`Go to logo ${i + 1}`}
              >
                {i === currentSlide && enableAutoscroll && (
                  <span
                    key={progressKey}
                    className={tickerStyles.dot__progress}
                    style={
                      {
                        '--progress-duration': `${autoscrollMs}ms`,
                        animationPlayState: isPaused ? 'paused' : 'running',
                      } as React.CSSProperties
                    }
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div
          className={showcaseStyles.static_grid}
          style={logoSpacing ? { gap: `${logoSpacing}px` } : undefined}
        >
          {childrenArray.map((child, i) => (
            <div
              key={i}
              className={cn(
                showcaseStyles.static_grid__item,
                showcaseStyles[`static_grid__item--${logoDisplay}`],
              )}
            >
              <div className={showcaseStyles.static_grid__inner} style={logoInnerStyle}>
                {child}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
